import type { Comment, CommentSubmittedPayload } from '~/types/blog'
import { emailHash } from '~/composables/useGravatar'

type LocalCommentNode = {
  ID: number
  parentId: number
  floor?: number
  replyToId?: number
  replyToAuthor?: string
  children?: LocalCommentNode[]
}

const cloneWithChildren = <T extends LocalCommentNode>(node: T, children: T[]): T => ({
  ...node,
  children
})

const prependAsRoot = <T extends LocalCommentNode>(comments: T[], node: T): T[] => {
  node.floor = comments.length + 1
  return [node, ...comments]
}

export const insertLocalComment = <T extends LocalCommentNode>(comments: T[], node: T, parentID: number): T[] => {
  if (parentID === 0) {
    return prependAsRoot(comments, node)
  }

  for (let rootIndex = 0; rootIndex < comments.length; rootIndex += 1) {
    const root = comments[rootIndex]
    if (root.ID !== parentID && !(root.children || []).some(item => item.ID === parentID)) {
      continue
    }

    const children = [...((root.children || []) as T[])]
    node.floor = children.length + 1
    const nextRoot = cloneWithChildren(root, [...children, node])
    return comments.map((item, index) => (index === rootIndex ? nextRoot : item))
  }

  node.parentId = 0
  node.replyToId = 0
  node.replyToAuthor = ''
  return prependAsRoot(comments, node)
}

// buildLocalSubmittedComment 将提交结果转换为前台评论树节点。
export const buildLocalSubmittedComment = (
  payload: CommentSubmittedPayload,
  replyTarget: Comment | null,
  nextTempID: () => number
): Comment => {
  const parentID = Number(payload.parentId || 0)
  return {
    ID: Number(payload.ID || 0) || nextTempID(),
    contentId: payload.contentId,
    parentId: parentID,
    author: payload.author,
    emailHash: payload.emailHash || emailHash(payload.email || ''),
    url: String(payload.url || ''),
    ip: '',
    ipRegion: '',
    agent: '',
    content: payload.content,
    status: payload.status || 'approved',
    userId: Number(payload.userId || 0),
    authenticated: payload.authenticated === true,
    emailNotify: payload.emailNotify ?? true,
    floor: 0,
    replyToId: parentID > 0 ? replyTarget?.ID || parentID : 0,
    replyToAuthor: parentID > 0 ? replyTarget?.author || '' : '',
    CreatedAt: payload.createdAt,
    children: [],
    localOnly: true
  }
}
